<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Emplacements</h1>
        <p class="page-subtitle">{{ rows.length }} emplacement{{ rows.length > 1 ? 's' : '' }} (maximum {{ maxStores }})</p>
      </div>
      <div>
        <!-- Création marchande masquée pour le moment : passer allowStoreCreation
             à true pour la réactiver. Les admins créent via kl_agent_dashboard. -->
        <v-btn v-if="enable && allowStoreCreation && rows.length < maxStores" color="primary" depressed small @click="create">
          <v-icon small left>mdi-plus</v-icon>
          Nouvel emplacement
        </v-btn>
        <v-chip v-else-if="enable && allowStoreCreation" small outlined color="grey">Limite de {{ maxStores }} atteinte</v-chip>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <v-card flat class="table-card">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">NOM</th>
              <th class="text-left">TÉLÉPHONE</th>
              <th class="text-left">E-MAIL</th>
              <th class="text-left">ADRESSE</th>
              <th class="text-center">COMMISSION</th>
              <th class="text-center">PRIX MIN</th>
              <th class="text-center">TAXE</th>
              <th class="text-center">DEVISE</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in rows" :key="s.id">
              <td class="text-left font-weight-medium">{{ s.name }}</td>
              <td class="text-left text-caption">{{ s.phone || '—' }}</td>
              <td class="text-left text-caption">{{ s.email || '—' }}</td>
              <td class="text-left text-caption">{{ s.address || '—' }}</td>
              <td class="text-center">
                <v-chip x-small :color="s.commission ? 'success' : 'error'" dark>{{ s.commission ? 'Actif' : 'Inactif' }}</v-chip>
              </td>
              <td class="text-center">
                <v-chip x-small :color="s.min_price ? 'success' : 'error'" dark>{{ s.min_price ? 'Actif' : 'Inactif' }}</v-chip>
              </td>
              <td class="text-center">{{ s.taxe }}%</td>
              <td class="text-center">{{ currencyLabel(s.rate_id) }}</td>
              <td style="width: 50px;">
                <v-btn icon x-small @click="edit(s)"><v-icon small color="primary">mdi-pencil</v-icon></v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </div>

    <!-- Edit dialog -->
    <v-dialog v-model="dialog" max-width="560" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-pencil</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Nom *</label>
                <v-text-field v-model="store.name" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']"></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Téléphone *</label>
                <v-text-field v-model="store.phone" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']"></v-text-field>
              </div>
            </v-col>
          </v-row>
          <div class="form-group">
            <label class="form-label">E-mail *</label>
            <v-text-field v-model="store.email" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']"></v-text-field>
          </div>
          <div class="form-group">
            <label class="form-label">Adresse *</label>
            <v-text-field v-model="store.address" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']"></v-text-field>
          </div>
          <v-row v-if="!creating">
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Commission agents</label>
                <v-select v-model="store.commission" :items="boolOptions" item-text="name" item-value="id" outlined dense hide-details></v-select>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Prix minimum</label>
                <v-select v-model="store.min_price" :items="boolOptions" item-text="name" item-value="id" outlined dense hide-details></v-select>
              </div>
            </v-col>
          </v-row>
          <div class="form-group" v-if="!creating">
            <label class="form-label">Taxe (%)</label>
            <v-text-field v-model.number="store.taxe" type="number" min="0" max="100" outlined dense hide-details></v-text-field>
          </div>
          <div class="form-group">
            <label class="form-label">Devise de l'emplacement</label>
            <v-select v-model="store.rate_id" :items="rateOptions" item-text="name" item-value="id" outlined dense hide-details></v-select>
          </div>
          <p v-if="creating" class="text-caption grey--text mb-0">
            Le nouvel emplacement reprend automatiquement les taux de change de votre
            emplacement principal. Les appareils s'y rattachent via les souscriptions.
          </p>

          <v-divider v-if="!creating" class="my-3"></v-divider>
          <p v-if="!creating" class="form-label" style="font-size: 0.85rem; color: #194173;">Options Restaurant</p>
          <v-row v-if="!creating">
            <v-col cols="6">
              <v-switch v-model="store.enable_table_management" label="Gestion des tables" dense hide-details class="mt-0"></v-switch>
            </v-col>
            <v-col cols="6">
              <v-switch v-model="store.enable_kitchen_printing" label="Impression cuisine" dense hide-details class="mt-0"></v-switch>
            </v-col>
            <v-col cols="6">
              <v-switch v-model="store.enable_bar_printing" label="Impression bar" dense hide-details class="mt-0"></v-switch>
            </v-col>
            <v-col cols="6">
              <v-switch v-model="store.enable_composed_products" label="Produits composés" dense hide-details class="mt-0"></v-switch>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="loading" :disabled="loading || !store.name || !store.phone || !store.email || !store.address" @click="save">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'StoresView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Emplacements', disabled: true, href: '/stores' }],
    enable: false,
    loading: false,
    index: -1,
    business_id: 0,
    form_title: "Modifier l'emplacement",
    creating: false,
    maxStores: 5,
    allowStoreCreation: false,
    boolOptions: [{ name: 'Actif', id: true }, { name: 'Inactif', id: false }],
    rateOptions: [{ name: 'HTG', id: 1 }, { name: 'USD', id: 2 }],
    rows: [],
    dialog: false,
    store: { name: '', address: '', phone: '', email: '', min_price: false, commission: false, taxe: 0, rate_id: 1 },
  }),
  methods: {
    currencyLabel(rateId) { return { 1: 'HTG', 2: 'USD' }[rateId] || '—' },
    load() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      this.$axios.get('/stores?orderasc=name&include=store_rates&business_id=' + this.business_id)
        .then(function (r) { self.rows = r.data; self.enable = true })
    },
    save() {
      if (!this.store.name || !this.store.phone || !this.store.email || !this.store.address) return
      var self = this; this.loading = true
      if (this.creating) {
        var payload = {
          business_id: this.business_id,
          name: this.store.name,
          phone: this.store.phone,
          email: this.store.email,
          address: this.store.address,
          rate_id: this.store.rate_id,
        }
        this.$axios.post('/stores?data=' + encodeURIComponent(JSON.stringify(payload)))
          .then(function () { self.dialog = false; self.loading = false; self.creating = false; self.reset(); self.load() })
          .catch(function () { self.loading = false })
        return
      }
      this.$axios.put('/stores/' + this.store.id + '?include=store_rates&data=' + encodeURIComponent(JSON.stringify(self.store)))
        .then(function (r) {
          Object.assign(self.rows[self.index], r.data)
          // Update cached store settings for sidebar
          var settings = { enable_table_management: self.store.enable_table_management || false, enable_kitchen_printing: self.store.enable_kitchen_printing || false, enable_bar_printing: self.store.enable_bar_printing || false, enable_composed_products: self.store.enable_composed_products || false }
          localStorage.setItem('storeSettings', JSON.stringify(settings))
          window.dispatchEvent(new CustomEvent('storeSettingsUpdated', { detail: settings }))
          self.dialog = false; self.loading = false; self.reset()
        })
        .catch(function () { self.loading = false })
    },
    edit(item) { this.creating = false; this.index = this.rows.indexOf(item); this.store = Object.assign({ rate_id: 1 }, item); this.form_title = 'Modifier : ' + this.store.name; this.dialog = true },
    create() { this.reset(); this.creating = true; this.form_title = 'Nouvel emplacement'; this.dialog = true },
    close() { this.dialog = false; this.creating = false; this.reset(); this.loading = false },
    reset() { this.store = { name: '', address: '', phone: '', email: '', min_price: false, commission: false, taxe: 0, rate_id: 1, enable_table_management: false, enable_kitchen_printing: false, enable_bar_printing: false, enable_composed_products: false }; this.index = -1 },
  },
  mounted() { this.load() },
}
</script>

<style scoped>
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.table-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.table-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.table-card .v-simple-table tbody tr:hover { background: rgba(25, 65, 115, 0.03) !important; }
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
</style>
